/**
 * Main entry point — jQuery variant.
 *
 * Assembles lMain from shared @lib modules and exposes it as window.lMain.
 * Also configures jQuery AJAX defaults and handlers for pages that load jQuery
 * (via prelude.js). Replaces code/public/js/core/main-2.js.
 */

import { getCoreVar, getDataVar } from '@lib/data';
import { ajax } from '@lib/ajax';
import { storeGet, storeHas, storeRemove, storeSet } from '@lib/store';
import { showFlash } from '@lib/flash';
import { initDevPersonaSiteSwitcher } from '@lib/dev-persona';

// ---------------------------------------------------------------------------
// lMain
// ---------------------------------------------------------------------------

const lMain = {
    getCoreVar,
    getDataVar,

    getUtmData(): string {
        return JSON.stringify({ cur: storeGet('rxc'), int: storeGet('rxi') });
    },

    storeGet,
    storeHas,
    storeRemove,
    storeSet,
    ajax,
};

window.lMain = lMain;

// ---------------------------------------------------------------------------
// jQuery AJAX setup
// ---------------------------------------------------------------------------

if (typeof jQuery !== 'undefined') {
    const ajaxCsrfToken =
        (document.querySelector('meta[name="ajax-token"]') as HTMLMetaElement | null)?.content ?? '';

    jQuery.ajaxSetup({
        type: 'POST',
        headers: { 'JQ': '1', 'X-CSRF-TOKEN': ajaxCsrfToken },
        timeout: 5000,
    });

    jQuery(document).ajaxSend(function (event, jqXHR, settings) {
        if (!settings.url || settings.url.includes('://')) {
            return;
        }
        const base = getCoreVar('base');
        if (/^(ajax\.php|tsf\.tsf)/i.test(settings.url)) {
            settings.url = base + settings.url;
            return;
        }
        settings.url = `${base}ajax.php?${settings.url}`;
    });

    // Note: the global ajaxError handler's 3rd arg is ajaxSettings, not textStatus.
    // jQuery sets jqXHR.statusText to 'parsererror', 'timeout', or 'abort' on errors.
    jQuery(document).ajaxError(function (event, jqXHR, _ajaxSettings, thrownError) {
        let msg: string;
        if (jqXHR.status === 0) {
            msg = 'Unable to connect to the backend. Please check your connection.';
        } else if ((jqXHR.responseJSON as { _error?: string } | undefined)?._error) {
            msg = `Failed to execute request: ${(jqXHR.responseJSON as { _error: string })._error}`;
        } else if (jqXHR.statusText === 'parsererror') {
            msg = 'Unable to execute request. A parse error occurred.';
        } else if (jqXHR.statusText === 'timeout') {
            msg = 'Unable to execute request. Connection to the backend timed out.';
        } else if (jqXHR.statusText === 'abort') {
            msg = 'Unable to execute request. Connection was aborted.';
        } else if (jqXHR.status !== 200) {
            msg = `Unable to execute request. Backend returned code ${jqXHR.status}`;
        } else {
            msg = 'An error occurred and your request could not be processed.';
            console.error('Error on Ajax request:', thrownError);
        }
        jQuery.toast({ text: msg, icon: 'error', position: 'top-left', hideAfter: 20000 });
    });

    jQuery(document).ajaxComplete(function (event, jqXHR) {
        const json = jqXHR.responseJSON as { flashmsg?: Parameters<typeof showFlash>[0] } | undefined;
        if (json?.flashmsg) {
            showFlash(json.flashmsg);
        }
    });

    // ---------------------------------------------------------------------------
    // Page load: UTM storage and Matomo visitor ID
    // ---------------------------------------------------------------------------

    jQuery(function () {
        initDevPersonaSiteSwitcher();

        if (window.location.search) {
            const query = new URLSearchParams(window.location.search);
            const utmParams = ['campaign', 'source', 'medium', 'content', 'term', 'id'] as const;
            const obj: Record<string, string | number> = {};

            for (const param of utmParams) {
                const value = query.get(`utm_${param}`);
                if (value) {
                    // Strip duplicated query strings embedded in the value (redirect edge case).
                    obj[param] = value.split('?')[0];
                }
            }

            if (Object.keys(obj).length > 0) {
                obj._time = Math.floor(Date.now() / 1000);
                storeSet('rxc', obj, true);
                if (!storeHas('rxi')) {
                    storeSet('rxi', obj);
                }
            }
        }

    });
}

// ---------------------------------------------------------------------------
// Misc globals
// ---------------------------------------------------------------------------

function setHashTo(x: string): void {
    location.hash = `#${x}`;
}
window.setHashTo = setHashTo;
